import { planRoute, getBestItinerary, parseItinerary, enrichWithRealTime } from './tmbPlannerService'

// ── Helpers de geometria ───────────────────────────────────────────────────────

export function haversineDistance(lat1, lng1, lat2, lng2) {
  const R  = 6371e3
  const φ1 = lat1 * Math.PI / 180
  const φ2 = lat2 * Math.PI / 180
  const Δφ = (lat2 - lat1) * Math.PI / 180
  const Δλ = (lng2 - lng1) * Math.PI / 180
  const a  = Math.sin(Δφ/2)**2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ/2)**2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

// ── Helpers de temps ──────────────────────────────────────────────────────────

function timeToMinuts(timeStr) {
  if (!timeStr) return 0
  const [h, m] = timeStr.split(':').map(Number)
  return (h || 0) * 60 + (m || 0)
}

function minutsToTime(mins) {
  const total = Math.max(0, mins)
  const h = Math.floor(total / 60) % 24
  const m = total % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

// Converteix minuts a format "09:00am" que entén el planner TMB
function minutsToAMPM(mins) {
  const total = Math.max(0, mins)
  const h  = Math.floor(total / 60) % 24
  const m  = total % 60
  const h12 = h % 12 || 12
  const ampm = h < 12 ? 'am' : 'pm'
  return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')}${ampm}`
}

const VEL          = { peu: 5000 / 60, transport: 20000 / 60 }
const SERVICE_MINS = 10
const ESTALVI_MIN  = 5

// Temps d'atenció per a l'hospital (usa la dificultat configurada a l'hospital)
export function tempsHospital(config) {
  return config?.temps_atencio?.[config?.hospital?.dificultat ?? 'normal'] ?? 30
}

function tempsPeuMin(dist) { return Math.round(dist / VEL.peu) }

function calcularHoraSegment(horaInici, legsAnteriors) {
  let mins = timeToMinuts(horaInici)
  for (const leg of legsAnteriors) {
    mins += (leg?.mins ?? 0) + SERVICE_MINS
  }
  return minutsToAMPM(mins)
}

// ── Nearest-neighbor greedy ───────────────────────────────────────────────────

function nearestNeighborOrder(doms) {
  if (doms.length < 2) return [...doms]
  const pendents = [...doms]
  const ruta     = [pendents.shift()]
  while (pendents.length) {
    const ult  = ruta[ruta.length - 1]
    let minD   = Infinity, minI = 0
    pendents.forEach((d, i) => {
      const d2 = haversineDistance(
        ult.dades_json.lat, ult.dades_json.lng,
        d.dades_json.lat,   d.dades_json.lng
      )
      if (d2 < minD) { minD = d2; minI = i }
    })
    ruta.push(pendents.splice(minI, 1)[0])
  }
  return ruta
}

// ── Restriccions de posició (primer / últim) ──────────────────────────────────

function aplicarRestriccionsPosicio(domicilis, restriccions) {
  if (!restriccions) return domicilis
  let resultat = [...domicilis]

  const codiPrimer = Object.entries(restriccions)
    .find(([k, r]) => k !== 'ordre' && r?.es_primer)?.[0]
  const codiUltim = Object.entries(restriccions)
    .find(([k, r]) => k !== 'ordre' && r?.es_ultim)?.[0]

  const clauDom = d => d.codi_visita || d.codi
  if (codiPrimer) {
    const idx = resultat.findIndex(d => clauDom(d) === codiPrimer)
    if (idx > 0) {
      const [dom] = resultat.splice(idx, 1)
      resultat.unshift(dom)
    }
  }

  if (codiUltim) {
    const idx = resultat.findIndex(d => clauDom(d) === codiUltim)
    if (idx !== -1 && idx !== resultat.length - 1) {
      const [dom] = resultat.splice(idx, 1)
      resultat.push(dom)
    }
  }

  return resultat
}

// ── Restriccions d'ordre ──────────────────────────────────────────────────────

function aplicarOrdreRestriccions(doms, restriccions) {
  if (!restriccions?.ordre?.length) return doms
  const arr = [...doms]
  const clauDom = d => d.codi_visita || d.codi
  let changed = true, iter = 0
  while (changed && iter++ < arr.length * 2) {
    changed = false
    for (const [before, after] of restriccions.ordre) {
      const iB = arr.findIndex(d => clauDom(d) === before)
      const iA = arr.findIndex(d => clauDom(d) === after)
      if (iB === -1 || iA === -1 || iB < iA) continue
      ;[arr[iB], arr[iA]] = [arr[iA], arr[iB]]
      changed = true
    }
  }
  return arr
}

// ── Restricció hospital ───────────────────────────────────────────────────────

// Migra camps antics hospital/temps_hospital als nous hospital_abans/temps_hospital_abans
function migrarRestriccions(restriccions) {
  if (!restriccions) return restriccions
  const migrades = {}
  for (const [codi, restr] of Object.entries(restriccions)) {
    if (codi === 'ordre') { migrades.ordre = restr; continue }
    migrades[codi] = {
      ...restr,
      hospital_abans:         restr.hospital_abans         ?? restr.hospital      ?? 'cap',
      temps_hospital_abans:   restr.temps_hospital_abans   ?? restr.temps_hospital ?? null,
      hospital_despres:       restr.hospital_despres       ?? 'cap',
      temps_hospital_despres: restr.temps_hospital_despres ?? null,
    }
  }
  return migrades
}

// Comptador per garantir codi_visita únic per instància d'hospital
// (resetejat a cada crida d'optimitzarRuta)
let _hospitalIdx = 0

function domHospital(hospital) {
  _hospitalIdx++
  return {
    codi:        'HOSPITAL',
    codi_visita: `HOSPITAL_${_hospitalIdx}`,
    adreça:      hospital.adreca,
    esHospital:  true,
    dades_json:  { lat: hospital.lat, lng: hospital.lng, persona_nom: hospital.nom },
  }
}

export function aplicarRestriccioHospital(domicilis, restriccions, hospital) {
  if (!hospital?.lat || !hospital?.lng) return domicilis
  const restrMigrades = migrarRestriccions(restriccions) ?? {}
  const resultat = [...domicilis]

  // Restriccions ABANS
  for (const [codi, restric] of Object.entries(restrMigrades)) {
    if (codi === 'ordre') continue
    const tipusAbans = restric.hospital_abans
    if (!tipusAbans || tipusAbans === 'cap') continue

    const indexDomicili = resultat.findIndex(d => d.codi === codi)
    if (indexDomicili === -1) continue

    if (tipusAbans === 'abans') {
      const hospitalJaVisitat = resultat.slice(0, indexDomicili).some(d => d.esHospital)
      if (!hospitalJaVisitat) resultat.unshift(domHospital(hospital))
    }

    if (tipusAbans === 'just_abans') {
      const indexHospital = resultat.findIndex(d => d.esHospital)
      if (indexHospital !== -1) resultat.splice(indexHospital, 1)
      const nouIndex = resultat.findIndex(d => d.codi === codi)
      resultat.splice(nouIndex, 0, domHospital(hospital))
    }
  }

  // Restriccions DESPRÉS
  for (const [codi, restric] of Object.entries(restrMigrades)) {
    if (codi === 'ordre') continue
    const tipusDespres = restric.hospital_despres
    if (!tipusDespres || tipusDespres === 'cap') continue

    const indexDomicili = resultat.findIndex(d => d.codi === codi)
    if (indexDomicili === -1) continue

    if (tipusDespres === 'despres') {
      const jaHiHaDespres = resultat.slice(indexDomicili + 1).some(d => d.esHospital)
      if (!jaHiHaDespres) resultat.push(domHospital(hospital))
    }

    if (tipusDespres === 'just_despres') {
      const nouIndex = resultat.findIndex(d => d.codi === codi)
      resultat.splice(nouIndex + 1, 0, domHospital(hospital))
    }
  }

  return resultat
}

// ── Arrivals amb finestres de temps ───────────────────────────────────────────

export function calcularArrivals(domicilis, legs, horaInici, restriccions) {
  let mins = timeToMinuts(horaInici)
  return domicilis.map((dom, i) => {
    const restr = restriccions?.[dom.codi] ?? null
    let espera  = 0
    let warning = null

    if (restr?.no_abans) {
      const limit = timeToMinuts(restr.no_abans)
      if (mins < limit) { espera = limit - mins; mins = limit }
    }
    if (restr?.hora_arribada) {
      const target = timeToMinuts(restr.hora_arribada)
      if (mins < target) { espera = Math.max(espera, target - mins); mins = target }
      else if (mins > target) {
        warning = `Arriba a ${minutsToTime(mins)}, previst ${restr.hora_arribada}`
      }
    }
    if (restr?.no_despres && !warning) {
      const limit = timeToMinuts(restr.no_despres)
      if (mins > limit) warning = `Arriba a ${minutsToTime(mins)}, límit ${restr.no_despres}`
    }

    const horaArribada = minutsToTime(mins)
    mins += SERVICE_MINS + (legs[i]?.mins ?? 0)
    return { codi: dom.codi, horaArribada, espera: espera > 0 ? espera : null, warning }
  })
}

// ── Leg factories ─────────────────────────────────────────────────────────────

function legPeu(dist) {
  return { tipus: 'peu', dist, mins: tempsPeuMin(dist), itinerari: null, linia: null, paradaOrigen: null, paradaDesti: null, tempsEspera: null }
}

function legPlanner(dist, parsed) {
  return {
    tipus:        'transport',
    dist,
    mins:         parsed.duracio_total,
    itinerari:    parsed,
    linia:        parsed.segments.find(s => s.tipus !== 'WALK')?.linia ?? null,
    paradaOrigen: null,
    paradaDesti:  null,
    tempsEspera:  null,
  }
}

// ── Mode TMB ──────────────────────────────────────────────────────────────────

function getModeParam(tipus) {
  switch (tipus) {
    case 'transport': return 'TRANSIT,WALK'
    case 'híbrida':   return 'TRANSIT,WALK'
    default:          return 'WALK'
  }
}

// ── Calculadors de legs (reben seqüència ja ordenada) ────────────────────────

function calcularLegsPeu(seq) {
  return seq.slice(0, -1).map((dom, i) => {
    const seg  = seq[i + 1]
    const dist = haversineDistance(
      dom.dades_json.lat, dom.dades_json.lng,
      seg.dades_json.lat, seg.dades_json.lng
    )
    return legPeu(dist)
  })
}

async function calcularLegsTransport(seq, horaInici, data) {
  const legs = []
  const mode = getModeParam('transport')
  for (let i = 0; i < seq.length - 1; i++) {
    const a    = seq[i], b = seq[i + 1]
    const dist = haversineDistance(
      a.dades_json.lat, a.dades_json.lng,
      b.dades_json.lat, b.dades_json.lng
    )
    const horaSegment = calcularHoraSegment(horaInici, legs)
    try {
      const itineraris = await planRoute(
        a.dades_json.lat, a.dades_json.lng,
        b.dades_json.lat, b.dades_json.lng,
        { time: horaSegment, mode, ...(data ? { date: new Date(data + 'T00:00:00') } : {}) }
      )
      const millor = getBestItinerary(itineraris)
      if (millor) {
        legs.push(legPlanner(dist, parseItinerary(millor)))
        continue
      }
    } catch (err) {
      console.warn(`[Optimizer] planRoute ${a.codi}→${b.codi}:`, err.message)
    }
    legs.push(legPeu(dist))
  }
  return legs
}

async function calcularLegsHibrida(seq, horaInici, data) {
  const legs = []
  const mode = getModeParam('híbrida')
  for (let i = 0; i < seq.length - 1; i++) {
    const a    = seq[i], b = seq[i + 1]
    const dist = haversineDistance(
      a.dades_json.lat, a.dades_json.lng,
      b.dades_json.lat, b.dades_json.lng
    )
    const minsPeu     = tempsPeuMin(dist)
    const horaSegment = calcularHoraSegment(horaInici, legs)
    try {
      const itineraris = await planRoute(
        a.dades_json.lat, a.dades_json.lng,
        b.dades_json.lat, b.dades_json.lng,
        { time: horaSegment, mode, ...(data ? { date: new Date(data + 'T00:00:00') } : {}) }
      )
      const millor = getBestItinerary(itineraris)
      if (millor) {
        const parsed = parseItinerary(millor)
        if (minsPeu - parsed.duracio_total >= ESTALVI_MIN) {
          legs.push(legPlanner(dist, parsed))
          continue
        }
      }
    } catch (err) {
      console.warn(`[Optimizer] planRoute ${a.codi}→${b.codi}:`, err.message)
    }
    legs.push(legPeu(dist))
  }
  return legs
}

// ── Wrappers exportats (ordenació + legs, sense hospital) ─────────────────────

export async function optimitzarRutaPeu(domicilis, restriccions) {
  const ordenats = aplicarOrdreRestriccions(nearestNeighborOrder(domicilis), restriccions)
  return { domicilis: ordenats, legs: calcularLegsPeu(ordenats) }
}

export async function optimitzarRutaTransport(domicilis, horaInici, restriccions) {
  const ordenats = aplicarOrdreRestriccions(nearestNeighborOrder(domicilis), restriccions)
  return { domicilis: ordenats, legs: await calcularLegsTransport(ordenats, horaInici) }
}

export async function optimitzarRutaHibrida(domicilis, horaInici, restriccions) {
  const ordenats = aplicarOrdreRestriccions(nearestNeighborOrder(domicilis), restriccions)
  return { domicilis: ordenats, legs: await calcularLegsHibrida(ordenats, horaInici) }
}

// ── Enriquiment temps real ────────────────────────────────────────────────────

export async function enrichSegmentsRealTime(legs) {
  return Promise.all(
    legs.map(async (leg) => {
      if (!leg.itinerari?.segments) return leg
      try {
        const enriched = await enrichWithRealTime(leg.itinerari)
        return { ...leg, itinerari: enriched }
      } catch {
        return leg
      }
    })
  )
}

// ── Expansió múltiples visites ────────────────────────────────────────────────

function expandirMultiplesVisites(domicilis) {
  const expandits = []
  for (const dom of domicilis) {
    const numVisites = dom.dades_json?.num_visites ?? 1
    const interval   = dom.dades_json?.interval_visites ?? 60
    for (let v = 1; v <= numVisites; v++) {
      expandits.push({
        ...dom,
        codi_visita:       `${dom.codi}_${v}`,
        visita_num:        v,
        visita_total:      numVisites,
        interval_anterior: v > 1 ? interval : null,
      })
    }
  }
  return expandits
}

function afegirRestriccionsMultiplesVisites(restriccions, domicilis) {
  const noves = restriccions ? { ...restriccions } : {}
  for (const dom of domicilis) {
    const numVisites = dom.dades_json?.num_visites ?? 1
    if (numVisites <= 1) continue
    const interval = dom.dades_json?.interval_visites ?? 60
    if (!noves.ordre) noves.ordre = []
    for (let v = 1; v < numVisites; v++) {
      noves.ordre.push([`${dom.codi}_${v}`, `${dom.codi}_${v + 1}`])
      noves[`${dom.codi}_${v + 1}`] = {
        ...(noves[`${dom.codi}_${v + 1}`] ?? {}),
        interval_minim_des_de: `${dom.codi}_${v}`,
        interval_minuts:       interval,
      }
    }
  }
  return Object.keys(noves).length > 0 ? noves : null
}

// ── Temps d'atenció i hores estimades ─────────────────────────────────────────

function tempsAtencio(punt, config) {
  if (punt.tipus === 'hospital') return tempsHospital(config)
  if (punt.tipus !== 'domicili' && !punt.codi_visita) return 0
  const dificultat = punt.dades_json?.dificultat ?? 'normal'
  let temps = config?.temps_atencio?.[dificultat] ?? SERVICE_MINS
  if (punt.dades_json?.estat === 'ingres') temps += config?.temps_ingres ?? 20
  if (punt.dades_json?.estat === 'alta')   temps += config?.temps_alta   ?? 15
  return temps
}

// Suma el temps que requereix cada domicili vinculat a un node hospital.
// Si no hi ha vinculats, torna el temps per defecte de config.
function calcularTempsHospitalNode(domicilisVinculats, restriccions, config) {
  if (!domicilisVinculats?.length) return tempsHospital(config)
  const restrMig = migrarRestriccions(restriccions) ?? {}
  return domicilisVinculats.reduce((total, codi) => {
    const r = restrMig[codi]
    const tempsRestr = r?.temps_hospital_abans ?? r?.temps_hospital_despres
    return total + (tempsRestr ?? config?.temps_atencio?.normal ?? 30)
  }, 0)
}

// Per a cada node hospital de la seqüència, retorna quins domicilis
// (amb restricció 'hospital') apareixen entre aquell hospital i el següent.
function assignarDomicilisAHospitals(sequencia, restriccions) {
  const restrMig   = migrarRestriccions(restriccions) ?? {}
  const assignacions = {}
  const hospitals = sequencia
    .map((p, idx) => ({ ...p, idx }))
    .filter(p => p.tipus === 'hospital')

  for (let h = 0; h < hospitals.length; h++) {
    const hosp     = hospitals[h]
    const hospNext = hospitals[h + 1]
    const clau     = hosp.codi_visita || `HOSPITAL_${h + 1}`
    assignacions[clau] = []

    for (let i = 0; i < sequencia.length; i++) {
      const p = sequencia[i]
      if (p.tipus !== 'domicili') continue
      const restr = restrMig[p.codi]

      // Domicili APRÈS d'aquest hospital (restricció 'abans' del domicili)
      const from = hosp.idx + 1
      const to   = hospNext ? hospNext.idx : sequencia.length
      if ((restr?.hospital_abans === 'abans' || restr?.hospital_abans === 'just_abans') && i >= from && i < to) {
        assignacions[clau].push(p.codi)
      }

      // Domicili ABANS d'aquest hospital (restricció 'despres' del domicili)
      if ((restr?.hospital_despres === 'despres' || restr?.hospital_despres === 'just_despres') && i < hosp.idx) {
        assignacions[clau].push(p.codi)
      }
    }
  }
  return assignacions
}

// Calcula les hores d'arribada i sortida estimades per a cada punt de la ruta.
// El paràmetre `restriccions` (opcional) és l'objecte de restriccions per codi
// (ex: { HADO1: { hospital: 'abans', temps_hospital: 15 }, ... }).
// S'usa per calcular el temps de cada visita hospital a partir dels domicilis vinculats.
export function calcularHoresEstimades(sequencia, legs, horaInici, config, restriccions = null) {
  let horaActual = timeToMinuts(horaInici ?? '09:00')
  const hores    = {}
  const assignacionsHospital = assignarDomicilisAHospitals(sequencia, restriccions)

  for (let i = 0; i < sequencia.length; i++) {
    const punt = sequencia[i]
    // Clau única per instància: usa codi_visita si disponible,
    // altrament `${codi}_${i}` per evitar col·lisions en punts repetits (hospital, inici)
    const clau = punt.codi_visita || `${punt.codi}_${i}`

    let tempsEst = 0
    let domicilisVinculats

    if (punt.tipus === 'hospital') {
      const vinculats = assignacionsHospital[clau] ?? []
      tempsEst = calcularTempsHospitalNode(vinculats, restriccions, config)
      if (vinculats.length > 0) domicilisVinculats = vinculats
    } else {
      tempsEst = tempsAtencio(punt, config)
    }

    hores[clau] = {
      hora_arribada: minutsToTime(horaActual),
      hora_sortida:  minutsToTime(horaActual + tempsEst),
      temps_estada:  tempsEst,
      ...(domicilisVinculats && { domicilis_vinculats: domicilisVinculats }),
    }
    horaActual += tempsEst
    if (legs?.[i]) horaActual += legs[i].mins ?? 0
  }
  return hores
}

// ── Dispatcher principal ───────────────────────────────────────────────────────

export async function optimitzarRuta(domicilis, tipus, horaInici = '09:00', data = null, restriccions = null, hospital = null, ubicacioInici = null, config = null) {
  _hospitalIdx = 0  // reinicia comptador per a aquesta crida

  // Expandeix domicilis amb múltiples visites
  const domicilisExpandits = expandirMultiplesVisites(domicilis)
  const restriccionsAmbVisites = afegirRestriccionsMultiplesVisites(restriccions, domicilis)

  const ambCoords   = domicilisExpandits.filter(d => d.dades_json?.lat && d.dades_json?.lng)
  const senseCoords = domicilisExpandits
    .filter(d => !d.dades_json?.lat || !d.dades_json?.lng)
    .sort((a, b) => a.codi.localeCompare(b.codi, undefined, { numeric: true }))

  if (ambCoords.length < 2) {
    const tot  = [
      ...ambCoords.sort((a, b) => a.codi.localeCompare(b.codi, undefined, { numeric: true })),
      ...senseCoords,
    ]
    const legs      = Array(Math.max(0, tot.length - 1)).fill(legPeu(0))
    const sequencia = tot.map(dom => tipificarPunt(dom))
    const hores_estimades = calcularHoresEstimades(sequencia, legs, horaInici, config, restriccionsAmbVisites)
    return { domicilis: tot, sequencia, legs, hores_estimades, arrivals: calcularArrivals(tot, legs, horaInici, restriccionsAmbVisites) }
  }

  // 1. Aplica restriccions de posició (primer/últim) i optimitza l'ordre
  const ambPosicio = aplicarRestriccionsPosicio(ambCoords, restriccionsAmbVisites)
  const hasPrimer  = restriccionsAmbVisites && Object.entries(restriccionsAmbVisites).some(([k, r]) => k !== 'ordre' && r?.es_primer)
  const hasUltim   = restriccionsAmbVisites && Object.entries(restriccionsAmbVisites).some(([k, r]) => k !== 'ordre' && r?.es_ultim)

  let ordenats
  if (hasPrimer || hasUltim) {
    const primer = hasPrimer ? [ambPosicio[0]] : []
    const ultim  = hasUltim  ? [ambPosicio[ambPosicio.length - 1]] : []
    const mig    = ambPosicio.slice(hasPrimer ? 1 : 0, hasUltim ? -1 : undefined)

    let migOrdenat
    if (mig.length >= 2 && primer.length > 0) {
      // Nearest-neighbor del mig seeded des del primer fix
      const ordered = nearestNeighborOrder([primer[0], ...mig])
      migOrdenat = ordered.slice(1)
    } else {
      migOrdenat = nearestNeighborOrder(mig)
    }
    ordenats = aplicarOrdreRestriccions([...primer, ...migOrdenat, ...ultim], restriccionsAmbVisites)
  } else {
    ordenats = aplicarOrdreRestriccions(nearestNeighborOrder(ambPosicio), restriccionsAmbVisites)
  }

  // 2. Insereix hospital a la posició correcta ABANS de calcular els legs
  const seqFinal = aplicarRestriccioHospital(ordenats, restriccionsAmbVisites, hospital)

  // 3. Calcula els legs per a la seqüència completa (inclou el waypoint hospital)
  let legs
  switch (tipus) {
    case 'transport': legs = await calcularLegsTransport(seqFinal, horaInici, data); break
    case 'híbrida':   legs = await calcularLegsHibrida(seqFinal, horaInici, data);   break
    default:          legs = calcularLegsPeu(seqFinal)
  }

  const domFinals       = [...seqFinal, ...senseCoords]
  const sequencia       = domFinals.map(dom => tipificarPunt(dom))
  const hores_estimades = calcularHoresEstimades(sequencia, legs, horaInici, config, restriccionsAmbVisites)
  return {
    domicilis: domFinals,
    sequencia,
    legs,
    hores_estimades,
    arrivals: calcularArrivals(domFinals, legs, horaInici, restriccionsAmbVisites),
  }
}

// ── Helper: converteix un domicili intern a punt tipificat per emmagatzemar ──

function tipificarPunt(dom) {
  if (dom.esHospital) {
    return {
      codi:        'HOSPITAL',
      codi_visita: dom.codi_visita ?? null,  // propagat des de domHospital (ex: 'HOSPITAL_1')
      tipus:       'hospital',
      id:          null,
      nom:         dom.dades_json?.persona_nom ?? null,
      adreca:      dom.adreça ?? null,
      lat:         dom.dades_json?.lat ?? null,
      lng:         dom.dades_json?.lng ?? null,
      dades_json:  null,
    }
  }
  return {
    codi:        dom.codi,
    codi_visita: dom.codi_visita  ?? null,
    visita_num:  dom.visita_num   ?? null,
    visita_total:dom.visita_total ?? null,
    tipus:       'domicili',
    id:          dom.id ?? null,
    nom:         dom.dades_json?.persona_nom ?? null,
    adreca:      dom.adreça ?? null,
    lat:         dom.dades_json?.lat ?? null,
    lng:         dom.dades_json?.lng ?? null,
    dades_json:  dom.dades_json ?? null,
  }
}

// ── Inserir revisita i recalcular els legs afectats ──────────────────────────
//
// Insereix nouPunt a la posició indexInsercio de la seqüència i recalcula
// ÚNICAMENT els 2 legs nous amb haversine (no cal cridar l'API per a revisites).
// Tots els legs existents fora dels 2 punts adjacents queden sense canvis.

export function inserirRevisita(sequencia, legs, indexInsercio, nouPunt) {
  const novaSequencia = [
    ...sequencia.slice(0, indexInsercio),
    nouPunt,
    ...sequencia.slice(indexInsercio),
  ]

  // Coordenades d'un punt tipificat (lat/lng pot estar al nivell arrel o a dades_json)
  function coords(p) {
    return {
      lat: p?.lat ?? p?.dades_json?.lat ?? null,
      lng: p?.lng ?? p?.dades_json?.lng ?? null,
    }
  }

  const cPrev = indexInsercio > 0
    ? coords(sequencia[indexInsercio - 1]) : null
  const cNou  = coords(nouPunt)
  const cNext = indexInsercio < sequencia.length
    ? coords(sequencia[indexInsercio]) : null

  const legPrevNou = (cPrev?.lat && cNou?.lat)
    ? legPeu(haversineDistance(cPrev.lat, cPrev.lng, cNou.lat, cNou.lng))
    : legPeu(0)
  const legNouNext = (cNou?.lat && cNext?.lat)
    ? legPeu(haversineDistance(cNou.lat, cNou.lng, cNext.lat, cNext.lng))
    : legPeu(0)

  // Substitueix el leg que hi havia entre prev i next pels dos nous
  const novaLegs = [
    ...legs.slice(0, Math.max(0, indexInsercio - 1)),
    ...(cPrev ? [legPrevNou] : []),
    ...(cNext ? [legNouNext] : []),
    ...legs.slice(indexInsercio),
  ]

  return { novaSequencia, novaLegs }
}

// ── Lògica Postposar ──────────────────────────────────────────────────────────

function horaActualStr() {
  const ara = new Date()
  return `${String(ara.getHours()).padStart(2,'0')}:${String(ara.getMinutes()).padStart(2,'0')}`
}
function dataActualStr() {
  return new Date().toISOString().slice(0, 10)
}

export async function postposarDomicili(ruta, codiVisita, tipusInici, domiciliIniciCodi, config, gpsCoords) {
  // 1. Determina el punt d'inici del recàlcul
  let iniciRecalcul
  switch (tipusInici) {
    case 'gps':
      iniciRecalcul = { tipus: 'custom', lat: gpsCoords?.lat, lng: gpsCoords?.lng, adreca: 'Posició actual' }
      break
    case 'domicili': {
      const dom = ruta.sequencia?.find(p => (p.codi_visita || p.codi) === domiciliIniciCodi)
      iniciRecalcul = { tipus: 'custom', lat: dom?.lat ?? dom?.dades_json?.lat, lng: dom?.lng ?? dom?.dades_json?.lng, adreca: dom?.adreca }
      break
    }
    case 'hospital':
      iniciRecalcul = { tipus: 'hospital', lat: config?.hospital?.lat, lng: config?.hospital?.lng, adreca: config?.hospital?.adreca }
      break
    case 'casa':
    default:
      iniciRecalcul = config?.ubicacio_inici
      break
  }

  // 2. Domicilis pendents (exclou realitzats i saltats)
  const pendents = (ruta.sequencia ?? []).filter(p => {
    const clau     = p.codi_visita || p.codi
    const estatDom = ruta.estat?.domicilis_estat?.[clau]?.estat
    return p.tipus === 'domicili' && estatDom !== 'realitzat' && estatDom !== 'saltat'
  })

  // 3. Si el postposat era el primer pendent, afegeix restricció d'ordre
  const restriccionsNoves = ruta.restriccions ? { ...ruta.restriccions } : {}
  const indexPostposat    = pendents.findIndex(p => (p.codi_visita || p.codi) === codiVisita)
  if (indexPostposat === 0 && pendents.length > 1) {
    const seguent = pendents[1]
    if (!restriccionsNoves.ordre) restriccionsNoves.ordre = []
    restriccionsNoves.ordre.push([(seguent.codi_visita || seguent.codi), codiVisita])
  }

  // 4. Converteix punts tipificats a pseudo-domicilis per a l'optimitzador
  const domsPendents = pendents.map(p => ({
    id:         p.id,
    codi:       p.codi,
    codi_visita:p.codi_visita,
    visita_num: p.visita_num,
    visita_total:p.visita_total,
    adreça:     p.adreca,
    dades_json: p.dades_json ?? { lat: p.lat, lng: p.lng },
  }))

  // 5. Recalcula ruta
  const novaRuta = await optimitzarRuta(
    domsPendents, ruta.tipus, horaActualStr(), dataActualStr(),
    Object.keys(restriccionsNoves).length > 0 ? restriccionsNoves : null,
    config?.hospital?.lat ? config.hospital : null,
    iniciRecalcul,
    config
  )

  // 6. Guarda modificació a l'historial
  const modificacio = {
    timestamp: new Date().toISOString(),
    tipus:     'postposat',
    codi:      codiVisita,
    posicio_inici: { tipus: tipusInici, ...iniciRecalcul },
    sequencia_anterior: (ruta.sequencia ?? []).map(p => p.codi_visita || p.codi),
    sequencia_nova:     novaRuta.sequencia.map(p => p.codi_visita || p.codi),
  }

  return {
    sequencia:      novaRuta.sequencia,
    legs:           novaRuta.legs,
    hores_estimades:novaRuta.hores_estimades,
    estat: {
      ...ruta.estat,
      domicilis_estat: {
        ...ruta.estat?.domicilis_estat,
        [codiVisita]: { ...(ruta.estat?.domicilis_estat?.[codiVisita] ?? {}), estat: 'postposat' },
      },
      modificacions: [...(ruta.estat?.modificacions ?? []), modificacio],
    },
  }
}
